// ─── Face-cover module: scope + health, for one camera ────────────────────────
//
// Used by the AI Modules page and by Manage Camera → Detectors. Two halves:
//
//   SCOPE   where and when this camera may raise a face-cover alert:
//           an alert area (head must be inside), excluded areas, active hours and
//           quiet hours. Saved to detector_config.face_cover via PUT /cameras/{idx},
//           the same body every module uses; an emptied list is sent as null so the
//           camera goes back to the default (whole frame / camera schedule).
//   HEALTH  GET /cameras/{idx}/face-cover/health — volume, labels, false % split
//           wrong vs allowed, flags, the overrides tuning has applied (with Reset),
//           the open suggestions (Apply / Ignore) and the near-misses to review.
//
// Nothing here changes detection on its own: suggestions apply only on a click, and
// go through the same apply → config history → rollback path as Telegram's.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import PatrolZoneEditor from './PatrolZoneEditor';
import { DayPicker } from './setup/SetupBits';

const C = {
  tx:   'var(--ind-tx, var(--text))',
  tx2:  'var(--ind-tx2, var(--text-muted))',
  line: 'var(--ind-line, var(--border))',
  card: 'var(--ind-ink, var(--surface-alt))',
  ok:   'var(--ind-ok, #22c55e)',
  bad:  'var(--ind-crit, #ef4444)',
  warn: '#f59e0b',
};
const box = { border: `1px solid ${C.line}`, borderRadius: 8, padding: 12, marginBottom: 12, background: C.card };
const btn = { padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.line}`, background: 'transparent',
              color: C.tx, cursor: 'pointer', fontSize: 12 };
const h4 = { margin: '0 0 8px', fontSize: 13, color: C.tx };

const pointsOf = z => (Array.isArray(z) ? z : (z && z.points)) || [];

function HoursList({ value, onChange, addLabel }) {
  const rows = Array.isArray(value) ? value : [];
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
          <DayPicker value={Array.isArray(r.days) ? r.days : undefined} onChange={d => set(i, { days: d })} />
          <input type="time" value={r.start || '00:00'} onChange={e => set(i, { start: e.target.value })} />
          <span style={{ color: C.tx2 }}>→</span>
          <input type="time" value={r.end || '23:59'} onChange={e => set(i, { end: e.target.value })} />
          <button type="button" style={btn} onClick={() => onChange(rows.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button type="button" style={btn}
        onClick={() => onChange([...rows, { days: [0, 1, 2, 3, 4, 5, 6], start: '20:00', end: '08:00' }])}>
        + {addLabel}
      </button>
      <span style={{ fontSize: 11, color: C.tx2, marginLeft: 8 }}>An end before the start runs past midnight.</span>
    </div>
  );
}

function NearMiss({ e, idx, onVerdict }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let live = true;
    if (e.proof) apiImageUrl(`/logs/detections/proof/${encodeURIComponent(e.proof)}`)
      .then(u => { if (live) setSrc(u); }).catch(() => {});
    return () => { live = false; };
  }, [e.proof]);
  const why = {
    clear_veto: 'face was seen clear first', min_samples: 'left before enough checks',
    min_secs: 'left too quickly', cover_ratio: 'covered only part of the time', track_lost: 'lost track',
    hand_only: 'hand at the mouth every time', eyes_hidden: 'eyes hidden (sunglasses?) — not alerted on yet',
  }[e.reason] || e.reason;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderTop: `1px solid ${C.line}` }}>
      {src ? <img src={src} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
           : <div style={{ width: 56, height: 56, background: C.line, borderRadius: 4 }} />}
      <div style={{ flex: 1, fontSize: 12, color: C.tx }}>
        {new Date((e.ts || 0) * 1000).toLocaleString()} — not alerted: <b>{why}</b>
        {e.verdict && <span style={{ color: C.tx2 }}> · marked {e.verdict === 'should_alert' ? 'should have alerted' : 'OK'}</span>}
      </div>
      <button type="button" style={btn} onClick={() => onVerdict(e.id, 'should_alert')}>Should have alerted</button>
      <button type="button" style={btn} onClick={() => onVerdict(e.id, 'ok_no_alert')}>OK</button>
    </div>
  );
}

export default function FaceCoverPanel({ cameraIdx, camera, onSaved }) {
  const stored = (camera?.detector_config || {}).face_cover || {};
  const [zone, setZone]       = useState(pointsOf((stored.zones || [])[0]));
  const [excl, setExcl]       = useState(stored.exclude_zones || []);
  const [drawExcl, setDraw]   = useState(null);           // points being drawn, or null
  const [active, setActive]   = useState(stored.active_hours || []);
  const [quiet, setQuiet]     = useState(stored.quiet_hours || []);
  const [health, setHealth]   = useState(null);
  const [near, setNear]       = useState([]);
  const [msg, setMsg]         = useState(null);
  const [busy, setBusy]       = useState(false);

  const loadHealth = useCallback(async () => {
    if (cameraIdx == null) return;
    try {
      const [h, n] = await Promise.all([
        apiFetch(`/cameras/${cameraIdx}/face-cover/health`).then(r => (r.ok ? r.json() : null)),
        apiFetch(`/cameras/${cameraIdx}/face-cover/near-misses`).then(r => (r.ok ? r.json() : null)),
      ]);
      setHealth(h);
      setNear((n && n.entries) || []);
    } catch { setHealth(null); }
  }, [cameraIdx]);
  useEffect(() => { loadHealth(); }, [loadHealth]);

  const call = async (path, body, okText) => {
    setBusy(true); setMsg(null);
    try {
      const r = await apiFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                                       body: JSON.stringify(body || {}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setMsg({ ok: true, text: okText });
      await loadHealth();
      onSaved && onSaved();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Failed' });
    } finally { setBusy(false); }
  };

  const saveScope = async () => {
    setBusy(true); setMsg(null);
    const orNull = v => (Array.isArray(v) && v.length ? v : null);
    const fc = {
      zones:         zone.length >= 3 ? [{ name: 'alert area', points: zone }] : null,
      exclude_zones: orNull(excl),
      active_hours:  orNull(active),
      quiet_hours:   orNull(quiet),
    };
    try {
      const r = await apiFetch(`/cameras/${cameraIdx}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ detector_config: { face_cover: fc } }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setMsg({ ok: true, text: 'Saved — the camera reloads to apply it.' });
      onSaved && onSaved();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Save failed' });
    } finally { setBusy(false); }
  };

  const h = health;
  const flagColour = { crit: C.bad, warn: C.warn, info: C.tx2 };

  return (
    <div>
      <div style={box}>
        <h4 style={h4}>😷 Where and when to alert</h4>
        <p style={{ fontSize: 12, color: C.tx2, margin: '0 0 8px' }}>
          Alert area: the person’s <b>head</b> must be inside it. Leave empty for the whole view.
        </p>
        <PatrolZoneEditor cameraIdx={cameraIdx} value={zone} onChange={setZone} />

        <h4 style={{ ...h4, marginTop: 12 }}>Excluded areas ({excl.length})</h4>
        {excl.map((z, i) => (
          <span key={i} style={{ ...btn, display: 'inline-block', marginRight: 6, marginBottom: 6 }}>
            {(z && z.name) || `Area ${i + 1}`}{' '}
            <a href="#remove" onClick={e => { e.preventDefault(); setExcl(excl.filter((_, j) => j !== i)); }}>✕</a>
          </span>
        ))}
        {drawExcl == null
          ? <button type="button" style={btn} onClick={() => setDraw([])}>+ Draw an excluded area</button>
          : (
            <div>
              <PatrolZoneEditor cameraIdx={cameraIdx} value={drawExcl} onChange={setDraw} />
              <button type="button" style={btn} disabled={drawExcl.length < 3}
                onClick={() => { setExcl([...excl, { name: `Area ${excl.length + 1}`, points: drawExcl }]); setDraw(null); }}>
                Add this area
              </button>{' '}
              <button type="button" style={btn} onClick={() => setDraw(null)}>Cancel</button>
            </div>
          )}

        <h4 style={{ ...h4, marginTop: 12 }}>Active hours</h4>
        <p style={{ fontSize: 12, color: C.tx2, margin: '0 0 6px' }}>
          Empty = follow the camera’s alert schedule. With “Ignore alert schedule” on, these replace it.
        </p>
        <HoursList value={active} onChange={setActive} addLabel="Add active hours" />
        <h4 style={{ ...h4, marginTop: 12 }}>Quiet hours</h4>
        <HoursList value={quiet} onChange={setQuiet} addLabel="Add quiet hours" />

        <div style={{ marginTop: 12 }}>
          <button type="button" style={{ ...btn, borderColor: C.ok, color: C.ok }} disabled={busy} onClick={saveScope}>
            Save where &amp; when
          </button>
          {msg && <span style={{ marginLeft: 10, fontSize: 12, color: msg.ok ? C.ok : C.bad }}>{msg.text}</span>}
        </div>
      </div>

      <div style={box}>
        <h4 style={h4}>📊 False-alert monitoring — last {h ? h.days : 7} days</h4>
        {!h && <p style={{ fontSize: 12, color: C.tx2 }}>No data yet.</p>}
        {h && (
          <>
            {(h.flags || []).map(f => (
              <div key={f.code} style={{ fontSize: 12, color: flagColour[f.level] || C.tx, marginBottom: 6 }}>
                {f.level === 'crit' ? '⛔' : f.level === 'warn' ? '⚠️' : 'ℹ️'} {f.text}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: C.tx, marginBottom: 8 }}>
              <span>Alerts <b>{h.alerts}</b> ({h.alerts_per_day}/day)</span>
              <span>Labelled <b>{h.labelled}</b></span>
              <span>False <b>{h.false_pct == null ? '—' : `${h.false_pct}%`}</b>
                {' '}(wrong {h.false_by_kind?.wrong || 0} · allowed {h.false_by_kind?.allowed || 0})</span>
              <span>Near-misses <b>{h.near_misses}</b> (should have alerted {h.should_have_alerted})</span>
              {h.config?.observe_only && <span>Learning mode: would have alerted <b>{h.observe_would_alert}</b>×</span>}
            </div>
            {h.rejects && Object.keys(h.rejects).length > 0 && (
              <div style={{ fontSize: 11, color: C.tx2, marginBottom: 8 }}>
                Judged {h.judged} faces · not judgeable: {Object.entries(h.rejects)
                  .sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} ${v}`).join(' · ')}
              </div>
            )}
            {h.config && Object.keys(h.config.overrides || {}).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: C.tx, marginBottom: 4 }}>
                  Overrides on <b>{h.config.sensitivity}</b> sensitivity:
                </div>
                {Object.entries(h.config.overrides).map(([k, o]) => (
                  <span key={k} style={{ ...btn, display: 'inline-block', marginRight: 6 }}>
                    {k} {o.value} <span style={{ color: C.tx2 }}>(preset {o.preset})</span>{' '}
                    <a href="#reset" onClick={e => { e.preventDefault();
                      call(`/cameras/${cameraIdx}/face-cover/reset-override`, { key: k }, `${k} back to the preset.`); }}>
                      Reset</a>
                  </span>
                ))}
              </div>
            )}
            {(h.suggestions || []).map(s => (
              <div key={s.sug_type} style={{ borderTop: `1px solid ${C.line}`, padding: '8px 0', fontSize: 12, color: C.tx }}>
                <b>{s.reason_label}</b> ×{s.count} — {s.reasoning}
                <div style={{ color: C.tx2, margin: '4px 0' }}>
                  {Object.keys(s.sugg_shown || {}).map(k => `${k}: ${JSON.stringify(s.cur_shown?.[k])} → ${JSON.stringify(s.sugg_shown[k])}`).join(' · ')}
                </div>
                <button type="button" style={{ ...btn, borderColor: C.ok, color: C.ok }} disabled={busy}
                  onClick={() => call(`/cameras/${cameraIdx}/face-cover/suggestions/${s.sug_type}/apply`, {}, 'Applied — in config history, can be rolled back.')}>
                  Apply</button>{' '}
                <button type="button" style={btn} disabled={busy}
                  onClick={() => call(`/cameras/${cameraIdx}/face-cover/suggestions/${s.sug_type}/ignore`, {}, 'Ignored.')}>
                  Ignore</button>
              </div>
            ))}
          </>
        )}
        {near.length > 0 && (
          <>
            <h4 style={{ ...h4, marginTop: 12 }}>Near-misses — covered faces that did not alert</h4>
            {near.slice(0, 20).map(e => (
              <NearMiss key={e.id} e={e} idx={cameraIdx}
                onVerdict={(id, v) => call(`/cameras/${cameraIdx}/face-cover/near-misses/${encodeURIComponent(id)}/verdict`,
                                           { verdict: v }, 'Thanks — logged.')} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
